import Vue from 'vue';
import Router from 'vue-router';
import nprogress from 'nprogress';

import store from '@/store';

import MainLayout from '@/components/layout/MainLayout.vue';
import { sendMessageToSentry } from '@/utilities/sentry.js';
import { trackPageView } from '@/utilities/gtm.js';

const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => err);
};

const originalReplace = Router.prototype.replace;
Router.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch((err) => err);
};

Vue.use(Router);

const router = new Router({
  mode: 'hash',
  base: '/',
  routes: [
    {
      path: '/',
      component: MainLayout,
      children: [
        {
          path: '',
          redirect: '/sign_in',
        },
        {
          path: 'account_no_password',
          name: 'auth/accountNoPassword',
          component: () => import('@/modules/auth/AccountNoPassword.vue'),
        },
        {
          path: 'add_email',
          name: 'auth/addEmail',
          component: () => import('@/modules/auth/containers/AddEmailContainer.vue'),
        },
      ],
    },
    {
      path: '/sign_up',
      component: MainLayout,
      children: [
        {
          path: '',
          name: 'auth/signUp',
          component: () => import('@/modules/auth/SignUp.vue'),
        },
        {
          path: 'by_phone',
          name: 'auth/signUpByPhone',
          component: () => import('@/modules/auth/containers/SignUpByPhoneContainer.vue'),
        },
        {
          path: 'phone_otp',
          name: 'auth/phoneOTPSignup',
          component: () => import('@/modules/auth/redesign/PhoneOTPSignup.vue'),
        },
        {
          path: 'by_email',
          name: 'auth/signUpByEmail',
          component: () => import('@/modules/auth/SignUpByEmail.vue'),
        },
        {
          path: 'email_conflict',
          name: 'auth/signUpEmailConflict',
          component: () => import('@/modules/auth/containers/EmailConflictContainer.vue'),
        },
        {
          path: 'email_otp',
          name: 'auth/signUpEmailOTP',
          component: () => import('@/modules/auth/redesign/SignUpEmailOTP.vue'),
        },
      ],
    },
    {
      path: '/sign_in',
      component: MainLayout,
      children: [
        {
          path: '',
          name: 'auth/signIn',
          component: () => import('@/modules/auth/containers/SignInContainer.vue'),
        },
        {
          path: 'by_phone',
          name: 'auth/signInByPhone',
          component: () => import('@/modules/auth/containers/SignInByPhoneContainer.vue'),
        },
        {
          path: 'phone_otp',
          name: 'auth/phoneOTPSignin',
          component: () => import('@/modules/auth/redesign/PhoneOTPSignin.vue'),
        },
        {
          path: 'by_email',
          name: 'auth/signInByEmail',
          component: () => import('@/modules/auth/containers/SignInByEmailContainer.vue'),
        },
        {
          path: 'forgot_pwd',
          name: 'auth/forgotPwd',
          component: () => import('@/modules/auth/redesign/ForgotPwd.vue'),
        },
        {
          path: 'forgot_pwd_sent',
          name: 'auth/forgotPwdSent',
          component: () => import('@/modules/auth/redesign/ForgotPwdSent.vue'),
        },
        {
          path: 'by_email_otp',
          name: 'auth/signInByEmailOTP',
          component: () => import('@/modules/auth/containers/SignInByEmailOTPContainer.vue'),
        },
        {
          path: '2fa_email',
          name: 'auth/2faEmail',
          component: () => import('@/modules/auth/redesign/TwoFA_Email.vue'),
        },
        {
          path: '2fa_phone',
          name: 'auth/2faPhone',
          component: () => import('@/modules/auth/redesign/TwoFA_Phone.vue'),
        },
        {
          path: '2fa_app',
          name: 'auth/2faApp',
          component: () => import('@/modules/auth/redesign/TwoFA_App.vue'),
        },
        {
          path: 'with_social_account',
          name: 'auth/signInWithSocialAccount',
          component: () => import('@/modules/auth/SignInWithSocialAccount.vue'),
        },
        {
          path: 'with_passwordless_account',
          name: 'auth/signInWithPasswordlessAccount',
          component: () => import('@/modules/auth/LinkEmailAccount.vue'),
        },
        {
          path: 'account_not_created',
          name: 'auth/signInFailWithSocialAccount',
          component: () => import('@/modules/auth/SignInFailWithSocialAccount.vue'),
        },
        {
          path: 'account_no_password',
          name: 'auth/signInAccountNoPassword',
          component: () => import('@/modules/auth/SignInAccountNoPassword.vue'),
        },
        {
          path: 'verify_email',
          name: 'auth/signInVerifyEmail',
          component: () => import('@/modules/auth/containers/SignInVerifyEmailContainer.vue'),
        },
      ],
    },
    {
      path: '/re_auth',
      component: MainLayout,
      children: [
        {
          path: '',
          name: 'auth/reAuthSignIn',
          component: () => import('@/modules/auth/ReAuthSignIn.vue'),
        },
        {
          path: 'by_phone',
          name: 'auth/reAuthSignInByPhone',
          component: () => import('@/modules/auth/ReAuthSignInByPhone.vue'),
        },
        {
          path: 'by_email',
          name: 'auth/reAuthSignInByEmail',
          component: () => import('@/modules/auth/ReAuthSignInByEmail.vue'),
        },
        {
          path: 'by_email_otp',
          name: 'auth/reAuthSignInByEmailOTP',
          component: () => import('@/modules/auth/ReAuthSignInByEmailOTP.vue'),
        },
        {
          path: 'by_social',
          name: 'auth/reAuthSignInBySocial',
          component: () => import('@/modules/auth/ReAuthSignInBySocial.vue'),
        },
        {
          path: 'verify_email',
          name: 'auth/reAuthSignInVerifyEmail',
          component: () => import('@/modules/auth/SignInVerifyEmail.vue'),
        },
      ],
    },
    {
      path: '/email_verification',
      component: MainLayout,
      children: [
        {
          path: 'expired',
          name: 'auth/emailVerificationExpired',
          component: () => import('@/modules/auth/EmailVerificationExpired.vue'),
        },
        {
          path: 'resent',
          name: 'auth/emailVerificationResent',
          component: () => import('@/modules/auth/EmailVerificationResent.vue'),
        },
        {
          path: 'success',
          name: 'auth/emailVerificationSuccess',
          component: () => import('@/modules/auth/EmailVerificationSuccess.vue'),
        },
      ],
    },
    {
      path: '/reset_pwd',
      component: MainLayout,
      children: [
        {
          path: '',
          name: 'auth/resetPwd',
          component: () => import('@/modules/auth/redesign/ResetPwd.vue'),
        },
        {
          path: 'success',
          name: 'auth/resetPwdSuccess',
          component: () => import('@/modules/auth/redesign/ResetPwdSuccess.vue'),
        },
      ],
    },
    {
      path: '/',
      component: MainLayout,
      children: [
        {
          path: 'return-to-app',
          name: 'auth/returnToApp',
          component: () => import('@/modules/auth/ReturnToApp.vue'),
        },
        {
          path: 'error',
          name: 'error',
          component: () => import('@/modules/error/ErrorPage.vue'),
        },
      ],
    },
    {
      path: '*',
      redirect: '/sign_in',
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  if (!navigator.userAgent.includes('uiWinMgr')) {
    nprogress.start();
  }

  try {
    store.dispatch('genVisitorId');
  } catch (error) {
    sendMessageToSentry('FingerprintScriptLoadFail', {
      level: 'error',
      extra: [
        {
          name: 'error',
          value: error?.response,
        },
      ],
    });
  }

  next();
});

router.afterEach(async (to) => {
  nprogress.done();
  
  // vue-meta updates document.title after navigation; wait for it before tracking.
  await Vue.nextTick();
  trackPageView(to);

});

export default router;
